import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, GitCompareArrows } from "lucide-react";

const CompareCtx = createContext(null);
const KEY = "os_compare";
export const COMPARE_CAP = 4;

export function CompareProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const has = useCallback((id) => items.some((i) => i.listing_id === id), [items]);
  const toggle = useCallback((listing) => {
    setItems((cur) => {
      if (cur.some((i) => i.listing_id === listing.listing_id)) return cur.filter((i) => i.listing_id !== listing.listing_id);
      if (cur.length >= COMPARE_CAP) return cur;
      return [...cur, listing];
    });
  }, []);
  const remove = useCallback((id) => setItems((cur) => cur.filter((i) => i.listing_id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  return (
    <CompareCtx.Provider value={{ items, has, toggle, remove, clear, cap: COMPARE_CAP, atCap: items.length >= COMPARE_CAP }}>
      {children}
    </CompareCtx.Provider>
  );
}

export const useCompare = () => useContext(CompareCtx);

export function CompareBar() {
  const compare = useCompare();
  const navigate = useNavigate();
  if (!compare || compare.items.length === 0) return null;
  const { items, remove, clear } = compare;
  return (
    <div className="fixed bottom-0 inset-x-0 z-[55] bg-obsidian/95 border-t border-olive backdrop-blur px-4 py-2.5" data-testid="compare-bar">
      <div className="max-w-[1200px] mx-auto flex items-center gap-3">
        <span className="font-mono text-[11px] uppercase text-olive-bright shrink-0 flex items-center gap-1.5">
          <GitCompareArrows className="w-4 h-4" /> Compare ({items.length})
        </span>
        <div className="flex items-center gap-2 overflow-x-auto flex-1 py-1">
          {items.map((i) => (
            <span key={i.listing_id} className="relative shrink-0" data-testid={`compare-bar-item-${i.listing_id}`}>
              {(i.photos || [])[0]
                ? <img src={i.photos[0]} className="w-9 h-9 object-cover border border-sniper" alt="" />
                : <span className="w-9 h-9 border border-sniper bg-surface block" />}
              <button onClick={() => remove(i.listing_id)} className="absolute -top-1.5 -right-1.5 bg-black text-blaze border border-sniper" title="Remove">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <button onClick={clear} className="font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground shrink-0">Clear</button>
        <button onClick={() => navigate("/compare")} disabled={items.length < 2} data-testid="compare-open"
          className="shrink-0 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
          Compare
        </button>
      </div>
    </div>
  );
}
